/* cart-toast.js — "Added to cart" toast for Ninja Patches (desktop + mobile).
   Ported from the Ninja Transfers mobile toast; adapted for Patches where the
   slideout cart is killed (settings.cart_action = "no-overlay") on BOTH
   breakpoints, so the toast must render on desktop AND mobile.

   window.showCartToast(addData) — addData is the /cart/add.js response (a single
   line-item object, or { items:[...] } for multi-variant adds). Pass the DELTA,
   never a full /cart.js / ?view=ajax cart (those report the whole cart, not what
   was just added).

   Thumbnail = the line item's own image: properties._cartImg (custom patches'
   uploaded design preview) || properties._Upload || featured_image. Each shown
   toast also persists a tiny payload to sessionStorage so the reload-based custom
   flows (?np_added / ?rtype=another) can replay it via window.npReplayCartToast()
   — see custom-uploader.js. */
(function () {
  var el, timer, styleInjected;
  /* C445 (Justin 2026-09-29): 2 seconds on screen (was 5); hover / touch still pauses it, leaving restarts it. */
  var SHOW_MS = 2000;

  /* ── Styles ─────────────────────────────────────────────────── */

  function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    var s = document.createElement('style');
    s.id = 'atc-toast-css';
    s.textContent = [
      /* C445 (Justin 2026-09-29): ninjatransfers.com's "Added to cart" styling (artwork-library-v2 .alv2-cartok —
         tinted head with a white check disc, thumb · name · qty · price row, no CTA) in the Patches green #329F66.
         Positioning is unchanged: mobile bottom-anchored + centered, desktop top-right. */
      '.atc-toast{--atc-green:#329F66;--atc-tint:#E8F5EE;--atc-ink:#2B2B2B;--nt-font-body:\'Inter\',-apple-system,system-ui,\'Segoe UI\',Roboto,sans-serif;position:fixed;bottom:calc(env(safe-area-inset-bottom,0px) + 16px);left:12px;right:12px;max-width:360px;margin-inline:auto;z-index:2147483647;background:#fff;border-radius:14px;box-shadow:0 12px 32px rgba(10,26,40,.22);opacity:0;transform:translateY(8px);transition:opacity .25s ease,transform .25s ease;pointer-events:none;overflow:hidden;font:600 14px/1.3 var(--nt-font-body);}',
      '.atc-toast.is-visible{opacity:1;transform:none;pointer-events:auto;}',
      /* Desktop: top-right anchored, slides down from the top. */
      '@media (min-width:750px){.atc-toast{top:16px;right:16px;bottom:auto;left:auto;margin:0;width:360px;max-width:calc(100vw - 32px);transform:translateY(-8px);}}',
      '.atc-head{display:flex;align-items:center;gap:10px;padding:12px 14px;background:var(--atc-tint);color:var(--atc-green);font-weight:700;}',
      '.atc-check{display:inline-grid;place-items:center;width:22px;height:22px;flex:none;border-radius:50%;background:#fff;color:var(--atc-green);}',
      '.atc-check svg{width:13px;height:13px;}',
      '.atc-head__label{font-size:14px;font-weight:700;color:var(--atc-green);line-height:1.3;}',
      '.atc-close{margin-left:auto;background:none;border:0;padding:0 2px;cursor:pointer;color:var(--atc-green);display:flex;align-items:center;justify-content:center;flex:none;}',
      '.atc-close svg{width:18px;height:18px;}',
      '.atc-body{display:flex;align-items:center;gap:12px;padding:14px;}',
      '.atc-thumb{width:56px;height:56px;flex:none;object-fit:contain;border-radius:10px;background:#f5f6f8;border:1px solid #E4E4E6;padding:4px;box-sizing:border-box;}',
      '.atc-info{flex:1;min-width:0;}',
      '.atc-name{display:block;color:var(--atc-ink);font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.atc-variant{display:block;margin-top:2px;color:#6b7280;font:500 12px/1.2 var(--nt-font-body);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}',
      '.atc-price{margin-left:auto;flex:none;color:var(--atc-ink);font-weight:700;white-space:nowrap;}',
      /* C457: several line items — one row each, divided, a touch tighter than the single row */
      '.atc-rows--multi .atc-body{padding:10px 14px;}',
      '.atc-rows--multi .atc-body+.atc-body{border-top:1px solid #EEF0F2;}',
      '.atc-more{padding:0 14px 12px;color:#6b7280;font:600 12px/1.3 var(--nt-font-body);}'
    ].join('\n');
    document.head.appendChild(s);
  }

  /* ── Normalize add response ─────────────────────────────────── */

  function normalizeAdd(addData) {
    if (!addData) return null;

    var items;
    if (Array.isArray(addData.items)) {
      items = addData.items;
    } else if (Array.isArray(addData)) {
      items = addData;
    } else if (addData.id) {
      items = [addData];
    } else {
      return null;
    }
    if (!items.length) return null;

    /* C457 (Justin 2026-09-30, "if there are multiple line items, multiple line items should show in the toast"): one row
       per line item added — each with its own thumb, name, variant · qty and price. The single-row summary fields stay
       (title/variant/qty/price/image = first line + the totals) for the reload stash. */
    var rows = items.map(rowOf);
    var totalQty = 0;
    var totalPrice = 0;
    for (var i = 0; i < rows.length; i++) {
      totalQty += rows[i].qty;
      totalPrice += rows[i].price;
    }
    var first = rows[0];
    return { title: first.title, variant: first.variant, qty: totalQty, price: totalPrice, image: first.image, rows: rows };
  }

  function rowOf(item) {
    var title = item.product_title || item.title || '';
    var rawVariant = item.variant_title || '';
    var variant = rawVariant.indexOf(' / ') !== -1
      ? rawVariant.split(' / ')[0]
      : rawVariant;
    if (variant) variant = variant.toLowerCase().replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); })
      /* C457: apparel sizes stay capitals — the title-casing above made XL "Xl" and 2XL "2xl" (now visible: every size is a row) */
      .replace(/\b(\d*X{0,3}[SML]|\d?XL)\b/gi, function (m) { return m.toUpperCase(); });

    /* Thumbnail source, in priority order: the custom-patch uploaded design preview
       (properties._cartImg), then the raw uploaded file (properties._Upload), then
       the line item's own product image. Custom patches carry _cartImg/_Upload;
       standard products fall through to featured_image. */
    var props = item.properties || {};
    var image = props._cartImg
      || props._Upload
      || item.image
      || (item.featured_image && item.featured_image.url)
      || '';

    return {
      title: title,
      variant: variant,
      qty: item.quantity || 1,
      price: item.final_line_price || item.line_price || item.price || 0,
      image: image
    };
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    var fmt = (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}';
    var dollars = (cents / 100).toFixed(2);
    var result = fmt
      .replace('{{amount_with_comma_separator}}', dollars.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
      .replace('{{amount}}', dollars);
    return result.replace(/<[^>]*>/g, '');
  }

  /* ── DOM construction ───────────────────────────────────────── */

  function getEl() {
    if (el && el.parentNode) return el;
    injectStyles();
    el = document.createElement('div');
    el.className = 'atc-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.innerHTML =
      '<div class="atc-head">' +
        '<span class="atc-check">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg>' +
        '</span>' +
        '<span class="atc-head__label">Added to cart</span>' +
        '<button type="button" class="atc-close" aria-label="Dismiss">' +
          '<svg viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="atc-rows"></div>';

    el.querySelector('.atc-close').addEventListener('click', function (e) {
      e.stopPropagation();
      hide();
    });
    el.addEventListener('click', function () { hide(); });
    el.addEventListener('touchstart', pauseTimer, { passive: true });
    el.addEventListener('mouseenter', pauseTimer);
    el.addEventListener('touchend', resumeTimer, { passive: true });
    el.addEventListener('mouseleave', resumeTimer);
    document.body.appendChild(el);
    return el;
  }

  /* ── Timer / visibility ─────────────────────────────────────── */

  function pauseTimer() {
    if (timer) { clearTimeout(timer); timer = null; }
  }

  function resumeTimer() {
    if (!el || !el.classList.contains('is-visible')) return;
    timer = setTimeout(hide, SHOW_MS);
  }

  function hide() {
    if (!el) return;
    el.classList.remove('is-visible');
    if (timer) { clearTimeout(timer); timer = null; }
  }
  /* Exposed so the reload-bound custom flows can dismiss a LINGERING toast (e.g. the design-1
     "Added to cart" still visible on its 2s timer) the instant a second-upload/reload begins —
     otherwise it flashes above the loading spinner on the dying page. See custom-uploader.js. */
  window.hideCartToast = hide;

  /* Render a normalized payload into the toast: its rows (C457), or the single {title, variant, qty, price, image}
     a replayed stash carries. A big multi-line add lists the first MAX_ROWS and says how many more. */
  var MAX_ROWS = 4;
  function rowEl(r) {
    var b = document.createElement('div');
    b.className = 'atc-body';
    b.innerHTML = '<img class="atc-thumb" alt="" /><div class="atc-info"><span class="atc-name"></span>' +
      '<span class="atc-variant"></span></div><span class="atc-price"></span>';
    b.querySelector('.atc-name').textContent = r.title;
    var variantParts = [];
    if (r.variant) variantParts.push(r.variant);
    variantParts.push('Qty ' + r.qty);
    b.querySelector('.atc-variant').textContent = variantParts.join(' · ');
    b.querySelector('.atc-price').textContent = formatMoney(r.price);
    var thumbEl = b.querySelector('.atc-thumb');
    if (r.image) { thumbEl.src = r.image; } else { thumbEl.style.display = 'none'; }
    return b;
  }
  function show(data) {
    var t = getEl();
    var rows = (data.rows && data.rows.length) ? data.rows : [data];
    var box = t.querySelector('.atc-rows');
    box.innerHTML = '';
    box.classList.toggle('atc-rows--multi', rows.length > 1);
    rows.slice(0, MAX_ROWS).forEach(function (r) { box.appendChild(rowEl(r)); });
    if (rows.length > MAX_ROWS) {
      var more = document.createElement('div');
      more.className = 'atc-more';
      var n = rows.length - MAX_ROWS;
      more.textContent = '+ ' + n + ' more ' + (n === 1 ? 'item' : 'items');
      box.appendChild(more);
    }

    t.classList.remove('is-visible');
    void t.offsetWidth;
    t.classList.add('is-visible');
    if (timer) clearTimeout(timer);
    timer = setTimeout(hide, SHOW_MS);
  }

  /* ── Reload-survival stash (custom ?np_added / ?rtype=another flows) ──────
     Those flows re-render the page, so the just-added line can't be matched by
     position (Shopify merges identical lines IN PLACE — "last line" is unsafe).
     Instead each shown toast persists its exact payload here; the arrival replays
     it via npReplayCartToast(). Tiny on purpose: URL only (never a data-URI), with
     a short TTL + clear-on-read so a stale payload can't surface on an unrelated
     later ?np_added navigation. */
  var STASH_KEY = 'npCartToast';
  var STASH_TTL = 180000; // 3 min

  function persist(data) {
    try {
      var img = data.image || '';
      if (img.indexOf('data:') === 0 || img.length > 2000) img = ''; // URL only — never a data-URI
      sessionStorage.setItem(STASH_KEY, JSON.stringify({ t: data.title, v: data.variant, q: data.qty, p: data.price, i: img, ts: Date.now() }));
    } catch (e) {}
  }

  /* ── Public API ─────────────────────────────────────────────── */

  window.showCartToast = function (addData) {
    var data = normalizeAdd(addData);
    if (!data) return;
    persist(data);
    /* DO-NOT-REVERT (Patches cart toast, blank-reload fix): the custom add-another flows reload
       the PDP (?np_added / ?rtype) to restart with the new design. Persist the stash (above) but
       SUPPRESS the pre-reload toast — the post-reload single owner replays it once, so it no
       longer flashes on the dying page. Both reload-pending markers (set before the add):
       __npSecondUploadInProgress (new-file second upload → ?np_added) and anotherTransfer (the
       .anotherTransfer / ?rtype path). See .claude/rules/patches-cart-toast.md */
    if (window.__npSecondUploadInProgress || window.anotherTransfer) return;
    show(data);
  };

  /* Replay the stashed payload after a reload-based custom add. Returns true if a
     fresh stash was shown. Clears the stash on read (even when stale); ignores
     anything older than the TTL. */
  window.npReplayCartToast = function () {
    var raw;
    try { raw = sessionStorage.getItem(STASH_KEY); sessionStorage.removeItem(STASH_KEY); } catch (e) { return false; }
    if (!raw) return false;
    var s;
    try { s = JSON.parse(raw); } catch (e) { return false; }
    if (!s || typeof s.ts !== 'number' || (Date.now() - s.ts) > STASH_TTL) return false;
    show({ title: s.t, variant: s.v, qty: s.q, price: s.p, image: s.i });
    return true;
  };

  /* ── Replay on custom reload arrivals ─────────────────────────────────────
     npReplayCartToast is the single replay function (URL marker + 3-min TTL + clear-on-read →
     no spurious replay). TRIGGER differs per flow:
     - ?np_added: replayed by custom-uploader.js AFTER the design preview (#fileupload_hero)
       renders + the np-arriving overlay clears, so the toast appears ONCE, after paint (not
       over the loading spinner). NOT fired here.
     - ?rtype=another: fired here at defer time (defensive; unreachable under no-overlay, where
       refreshAll_ never runs). Safe — this defer script has defined npReplayCartToast by now. */
  if (location.search.indexOf('rtype=another') > -1) {
    window.npReplayCartToast();
  }
})();
